import { useGeckoAuth } from 'features/auth'
import { createClassObjForEntryJson } from 'features/rolodex/rolodexEntryDispenser'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import { useContext } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'
import { z } from 'zod'

import { RolodexEntryQueryBody } from './useRolodexEntries'

interface RolodexAiQuery {
  query: string
  load_options: RolodexEntryQueryBody
  top_k: number
  re_rank: boolean
  include_embeddings: boolean
}

// Zod schema for RolodexEntryJson
const Entry = z.object({
  id: z.string(),
  type: z.nativeEnum(RolodexEntryType),
  data: z.record(z.string(), z.unknown()),
  tags: z.record(z.string(), z.string().nullable()),
  index: z.record(z.string(), z.string()),
  generation: z.number(),
  updated_at: z.string(),
  created_at: z.string().nullish(),
  created_by: z.string().nullable().optional(),
  last_updated_by: z.string().nullable().optional(),
  access_restriction: z.union([z.literal('internal'), z.null()]),
})

// Enum for embedding types
const EmbeddingType = z.enum([
  'inline_text_summary',
  'inline_image_summary',
  'image_summary',
  'inline_text_raw',
  'image_multimodal',
])

// Base metadata shared by most embedding types
const EmbeddingRowMetadataBase = z.object({
  file_ref_id: z.string().nullable().optional(),
  entry_id: z.string(),
  file_ref_path: z.string(),
  organization: z.string(),
  embed_model_name: z.string(),
  updated_at: z.coerce.date(),
  embedding_type: EmbeddingType.nullable().optional(),
  text_content: z.string().nullable().optional(),
  chunk_index: z.number().int().nullable().optional(),
  page_number: z.number().int().nullable().optional(),
  section: z.string().nullable().optional(),
})

// InlineTextSummaryMetadata and InlineImageSummaryMetadata (same shape)
const InlineChunkMetadata = EmbeddingRowMetadataBase.extend({
  text_content: z.string(),
  chunk_index: z.number().int(),
  page_number: z.number().int(),
  section: z.string(),
  file_ref_id: z.string().nullable().optional(),
})

// ImageSummaryMetadata
const ImageSummaryMetadata = EmbeddingRowMetadataBase.extend({
  text_content: z.string(),
})

// ImageEmbeddingMetadata (same as base)
const ImageEmbeddingMetadata = EmbeddingRowMetadataBase

// FastDocumentChunkMetadata (different base structure)
const FastDocumentChunkMetadata = z.object({
  entry_id: z.string(),
  file_ref_path: z.string(),
  organization: z.string(),
  embed_model_name: z.string(),
  updated_at: z.coerce.date(),
  embedding_type: EmbeddingType.nullable().optional(),
  text_content: z.string(),
  page_number: z.number().int(),
  file_ref_id: z.string().nullable().optional(),
  chunk_index: z.number().int().nullable().optional(),
  section: z.string().nullable().optional(),
})

// Union of all metadata types
const EmbeddingMetadata = z.union([
  InlineChunkMetadata,
  ImageSummaryMetadata,
  ImageEmbeddingMetadata,
  FastDocumentChunkMetadata,
])

// EmbeddingRow
const EmbeddingRow = z.object({
  id: z.string().nullable().optional(),
  embedding: z.array(z.number()).optional(),
  metadata: EmbeddingMetadata,
})

// EmbeddingResult extends EmbeddingRow
const EmbeddingResult = EmbeddingRow.extend({
  score: z.number(),
  re_rank_used: z.boolean().default(false),
})

// CantileverAiQueryResponse
const CantileverAiQueryResponse = z.object({
  success: z.boolean(),
  results: z.array(EmbeddingResult),
  entries: z.array(Entry),
  related_entries: z.array(Entry),
})

// Export types
export type EmbeddingType = z.infer<typeof EmbeddingType>
export type EmbeddingResult = z.infer<typeof EmbeddingResult>
export type CantileverAiQueryResponse = z.infer<
  typeof CantileverAiQueryResponse
>

export {
  CantileverAiQueryResponse,
  EmbeddingMetadata,
  EmbeddingResult,
  EmbeddingRow,
  EmbeddingType,
  Entry,
}

export type ParsedCantileverAiQueryResult = CantileverAiQueryResponse & {
  entries: ReadonlyArray<GeckoEntry>
  relatedEntries: ReadonlyArray<GeckoEntry>
}

/**
 * A hook for querying the rolodex AI API. This is NOT an SWR because it's an expensive API call,
 * and so should happen as the result of a user action, not automatically.
 * @returns A function that can be used to query the rolodex AI API
 */
export function useRolodexAi(): (
  query: RolodexAiQuery
) => Promise<ParsedCantileverAiQueryResult> {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { apiURL } = useContext(RolodexContext)

  return async (query: RolodexAiQuery) => {
    const response = await axios.post(
      `${apiURL}/api/v2/cantilever-ai/query`,
      query
    )
    const parsed = CantileverAiQueryResponse.parse(response.data)

    // Convert RolodexEntryJson to GeckoEntry using the dispenser's conversion function
    const entries = parsed.entries
      .map((entry) => ({
        ...entry,
        created_at: entry.created_at ?? undefined,
        created_by: entry.created_by ?? undefined,
        last_updated_by: entry.last_updated_by ?? undefined,
      }))
      .map(createClassObjForEntryJson)
      .filter((entry): entry is GeckoEntry => entry !== undefined)

    const relatedEntries = parsed.related_entries
      .map((entry) => ({
        ...entry,
        created_at: entry.created_at ?? undefined,
        created_by: entry.created_by ?? undefined,
        last_updated_by: entry.last_updated_by ?? undefined,
      }))
      .map(createClassObjForEntryJson)
      .filter((entry): entry is GeckoEntry => entry !== undefined)

    return {
      ...parsed,
      entries,
      relatedEntries,
    }
  }
}
